import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import type {
  PermissionAction,
  PermissionModule,
  PermissionMatrixEntry,
} from "../config/permissions";

interface User {
  id: string;
  username: string;
  email: string;
  role?: string;
  roleId?: string;
  isSuperAdmin: boolean;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  permissions: PermissionMatrixEntry[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  hasPermission: (module: PermissionModule, action: PermissionAction) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const buildHasPermission = (user: User | null) => {
  return (module: PermissionModule, action: PermissionAction) => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    return user.permissions?.some(
      (permission) => permission.module === module && permission.actions[action]
    );
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
  };

  const logout = () => {
    clearSession();
  };

  const normalizeUser = (rawUser: User): User => ({
    ...rawUser,
    isSuperAdmin: Boolean(rawUser.isSuperAdmin),
    permissions: rawUser.permissions ?? [],
  });

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      if (!response.data?.success) {
        clearSession();
        return;
      }
      const normalizedUser = normalizeUser(response.data.user);
      setUser(normalizedUser);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
    } catch (error) {
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  // Charger le token et l'utilisateur depuis localStorage au démarrage
  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (!storedToken) {
      setLoading(false);
      return;
    }

    setToken(storedToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    fetchCurrentUser();
  }, []);

  // Intercepteur global: déconnecte sur 401 (token expiré ou invalide)
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url: string = error?.config?.url || "";
        const isAuthCall = url.includes("/auth/login") || url.includes("/auth/setup");
        if (status === 401 && !isAuthCall) {
          clearSession();
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.replace("/login");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const login = async (username: string, password: string) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      username,
      password,
    });

    if (response.data.success) {
      const { token: newToken, user: newUser } = response.data;

      const normalizedUser: User = normalizeUser(newUser);
      setToken(newToken);
      setUser(normalizedUser);

      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(normalizedUser));

      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    }
  };

  const hasPermission = buildHasPermission(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        login,
        logout,
        loading,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export default AuthContext;

